import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import api from '../services/api';
import { API_ENDPOINTS, processProductsArray } from '../utils/constants';
import { 
  FiSearch, 
  FiFilter, 
  FiX, 
  FiChevronLeft, 
  FiChevronRight 
} from 'react-icons/fi';

const ProductsTwo = () => {
  const location = useLocation();
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  
  // Price filter states
  const [tempMinPrice, setTempMinPrice] = useState('');
  const [tempMaxPrice, setTempMaxPrice] = useState('');
  const [appliedMinPrice, setAppliedMinPrice] = useState('');
  const [appliedMaxPrice, setAppliedMaxPrice] = useState('');
  
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  const itemsPerPage = 20;
  const categoryScrollRef = useRef(null);
  const lastProductRef = useRef(null);
  const observerRef = useRef(null);
useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Function to check if product has "បោះដុំ" in title
  const hasWholesaleText = (product) => {
    if (product && product.title) {
      return product.title.includes('បោះដុំ');
    }
    return false;
  };

  // Function to filter ONLY products with "បោះដុំ" in title
  const filterOnlyWholesaleProducts = (products) => {
    return products.filter(product => hasWholesaleText(product));
  };

  // Get search query from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('search');
    if (searchQuery) {
      setSearchTerm(searchQuery);
      setDebouncedSearch(searchQuery);
    }
  }, [location.search]);

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Apply filters whenever filter criteria change
  useEffect(() => {
    if (allProducts.length > 0) {
      applyFiltersAndSort();
    }
  }, [selectedCategory, debouncedSearch, appliedMinPrice, appliedMaxPrice, sortBy, allProducts]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
  }, [selectedCategory, debouncedSearch, appliedMinPrice, appliedMaxPrice, sortBy]);

  // Update display products when filtered products or page changes
  useEffect(() => {
    const startIndex = 0;
    const endIndex = currentPage * itemsPerPage;
    setDisplayProducts(filteredProducts.slice(startIndex, endIndex));
    setHasMore(endIndex < filteredProducts.length);
  }, [filteredProducts, currentPage]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    if (loadingMore) return;
    
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        loadMoreProducts();
      }
    }, options);

    if (lastProductRef.current) {
      observer.observe(lastProductRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [displayProducts, hasMore, loadingMore, loading]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch categories
      const categoriesResponse = await api.get(API_ENDPOINTS.CATEGORIES);
      setCategories(categoriesResponse.data);
      
      // Fetch all products
      const productsResponse = await api.get(API_ENDPOINTS.PRODUCTS, {
        params: { limit: 10000 }
      });
      
      let productsData = [];
      if (productsResponse.data.data && Array.isArray(productsResponse.data.data)) {
        productsData = productsResponse.data.data;
      } else if (Array.isArray(productsResponse.data)) {
        productsData = productsResponse.data;
      }
      
      // Filter ONLY products with "បោះដុំ" in title
      const wholesaleOnlyProducts = filterOnlyWholesaleProducts(productsData);
      
      // Process products (add image URLs, etc.)
      const processedProducts = processProductsArray(wholesaleOnlyProducts);
      setAllProducts(processedProducts);
      
      // Initial filter
      applyFiltersAndSort(processedProducts);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('មិនអាចផ្ទុកផលិតផលបានទេ');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = (products = allProducts) => {
    let filtered = [...products];
    
    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(product => product.category_id === selectedCategory);
    }
    
    // Filter by search term
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter(product => 
        product.title.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter by price range
    if (appliedMinPrice && appliedMinPrice !== '') {
      const minPrice = parseFloat(appliedMinPrice);
      filtered = filtered.filter(product => product.price >= minPrice);
    }
    
    if (appliedMaxPrice && appliedMaxPrice !== '') {
      const maxPrice = parseFloat(appliedMaxPrice);
      filtered = filtered.filter(product => product.price <= maxPrice);
    }
    
    // Sort products
    switch (sortBy) {
      case 'price_low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price_high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'popular':
        filtered.sort((a, b) => (b.sales_count || 0) - (a.sales_count || 0));
        break;
      default:
        break;
    }
    
    setFilteredProducts(filtered);
  };

  const loadMoreProducts = () => {
    if (!hasMore || loadingMore) return;
    
    setLoadingMore(true);
    setCurrentPage(prev => prev + 1);
    setTimeout(() => setLoadingMore(false), 100);
  };

  // Apply price filter
  const applyPriceFilter = () => {
    // Validate min <= max
    if (tempMinPrice && tempMaxPrice && parseFloat(tempMinPrice) > parseFloat(tempMaxPrice)) {
      setError('តម្លៃតិចបំផុតមិនអាចធំជាងតម្លៃច្រើនបំផុត');
      return;
    }
    
    setAppliedMinPrice(tempMinPrice);
    setAppliedMaxPrice(tempMaxPrice);
    setError(null);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setSelectedCategory('');
    setSortBy('newest');
    setTempMinPrice('');
    setTempMaxPrice('');
    setAppliedMinPrice('');
    setAppliedMaxPrice('');
  };

  // Clear price filter
  const clearPriceFilter = () => {
    setTempMinPrice('');
    setTempMaxPrice('');
    setAppliedMinPrice('');
    setAppliedMaxPrice('');
  };

  const hasActiveFilters = searchTerm || selectedCategory || appliedMinPrice || appliedMaxPrice;

  const scrollCategory = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = categoryScrollRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      categoryScrollRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
          ផលិតផល<span className="text-red-600">បោះដុំ</span>
        </h1>
        <p className="text-gray-500 text-sm">
          ស្វែងរកផលិតផលបោះដុំពី GrandFatherSmile ក្នុងតម្លៃពិសេស
        </p>
        {debouncedSearch && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-gray-500">លទ្ធផលស្វែងរកសម្រាប់:</span>
            <span className="text-sm font-medium text-red-600">"{debouncedSearch}"</span>
            <button 
              onClick={() => setSearchTerm('')}
              className="text-xs text-gray-400 hover:text-red-600"
            >
              (សម្អាត)
            </button>
          </div>
        )}
      </div>

      {/* Mobile Category Horizontal Scroll */}
      <div className="lg:hidden mb-6 relative">
        <div className="flex items-center">
          {categories.length > 4 && (
            <button 
              onClick={() => scrollCategory('left')}
              className="absolute left-0 z-10 w-8 h-8 bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:bg-red-50 hover:text-red-600 hover:border-red-600 rounded"
            >
              <FiChevronLeft size={18} />
            </button>
          )}
          
          <div 
            ref={categoryScrollRef}
            className="flex gap-2 overflow-x-auto scrollbar-hide px-8"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <button
              onClick={() => setSelectedCategory('')}
              className={`flex-none px-4 py-2 text-sm border whitespace-nowrap rounded ${
                selectedCategory === '' 
                  ? 'bg-red-600 text-white border-red-600' 
                  : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-600'
              }`}
            >
              ទាំងអស់
            </button>
            
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex-none px-4 py-2 text-sm border whitespace-nowrap rounded ${
                  selectedCategory === category.id 
                    ? 'bg-red-600 text-white border-red-600' 
                    : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-600'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {categories.length > 4 && (
            <button 
              onClick={() => scrollCategory('right')}
              className="absolute right-0 z-10 w-8 h-8 bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:bg-red-50 hover:text-red-600 hover:border-red-600 rounded"
            >
              <FiChevronRight size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="ស្វែងរកផលិតផលបោះដុំ... (អង្ករ, ត្រី, បន្លែ, ផ្លែឈើ)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-12 pl-10 pr-4 border border-gray-200 focus:outline-none focus:border-red-600 focus:bg-white rounded"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600"
            >
              <FiX size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Desktop Sidebar Filters */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-24 border border-gray-200 p-5 bg-white rounded">
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-200">
              <span className="font-semibold text-gray-800">តម្រង</span>
              {hasActiveFilters && (
                <button 
                  onClick={clearFilters}
                  className="text-xs text-red-600 hover:text-red-700 underline"
                >
                  សម្អាតទាំងអស់
                </button>
              )}
            </div>

            {/* Categories */}
            <div className="mb-6 pb-5 border-b border-gray-200">
              <h3 className="font-medium text-gray-700 mb-3">ប្រភេទ</h3>
              <div className="space-y-2">
                <div 
                  onClick={() => setSelectedCategory('')}
                  className={`flex items-center gap-2 cursor-pointer ${
                    selectedCategory === '' ? 'text-red-600' : 'text-gray-600 hover:text-red-600'
                  }`}
                >
                  <div className={`w-4 h-4 border rounded ${
                    selectedCategory === '' 
                      ? 'bg-red-600 border-red-600' 
                      : 'border-gray-300'
                  }`}>
                    {selectedCategory === '' && (
                      <span className="flex items-center justify-center text-white text-xs">✓</span>
                    )}
                  </div>
                  <span className="text-sm">ទាំងអស់</span>
                </div>

                {categories.map((category) => (
                  <div 
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center gap-2 cursor-pointer ${
                      selectedCategory === category.id ? 'text-red-600' : 'text-gray-600 hover:text-red-600'
                    }`}
                  >
                    <div className={`w-4 h-4 border rounded ${
                      selectedCategory === category.id 
                        ? 'bg-red-600 border-red-600' 
                        : 'border-gray-300'
                    }`}>
                      {selectedCategory === category.id && (
                        <span className="flex items-center justify-center text-white text-xs">✓</span>
                      )}
                    </div>
                    <span className="text-sm">{category.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div className="mb-6 pb-5 border-b border-gray-200">
              <h3 className="font-medium text-gray-700 mb-3">តម្លៃ</h3>
              {error && error.includes('តម្លៃ') && (
                <div className="mb-3 text-xs text-red-600 bg-red-50 p-2 rounded">
                  {error}
                </div>
              )}
              <div className="flex gap-2 mb-2">
                <input
                  type="number"
                  placeholder="តិចបំផុត"
                  value={tempMinPrice}
                  onChange={(e) => setTempMinPrice(e.target.value)}
                  className="w-1/2 px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-red-600 rounded"
                />
                <input
                  type="number"
                  placeholder="ច្រើនបំផុត"
                  value={tempMaxPrice}
                  onChange={(e) => setTempMaxPrice(e.target.value)}
                  className="w-1/2 px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-red-600 rounded"
                />
              </div>
              <button 
                onClick={applyPriceFilter}
                className="w-full bg-gray-100 border border-gray-200 py-2 text-sm hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors rounded"
              >
                អនុវត្ត
              </button>
            </div>

            {/* Sort */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">តម្រៀបតាម</h3>
              <div className="space-y-2">
                {[
                  { value: 'newest', label: 'ថ្មីបំផុត' },
                  { value: 'price_low', label: 'តម្លៃថោកបំផុត' },
                  { value: 'price_high', label: 'តម្លៃថ្លៃបំផុត' },
                  { value: 'popular', label: 'ពេញនិយម' }
                ].map((option) => (
                  <div 
                    key={option.value}
                    onClick={() => setSortBy(option.value)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="sort"
                      checked={sortBy === option.value}
                      onChange={() => {}}
                      className="accent-red-600"
                    />
                    <span className="text-sm text-gray-600">{option.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200">
            <div className="text-sm text-gray-500">
              <span className="text-lg font-semibold text-gray-800">{filteredProducts.length}</span> ផលិតផលបោះដុំ
            </div>
            
            {/* Mobile Filter Button */}
            <button 
              onClick={() => setShowMobileFilters(true)}
              className="lg:hidden flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-200 text-sm hover:bg-red-50 hover:text-red-600 hover:border-red-600 rounded"
            >
              <FiFilter size={16} />
              តម្រង
              {hasActiveFilters && (
                <span className="w-2 h-2 bg-red-600 rounded-full"></span>
              )}
            </button>

            {/* Desktop Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="hidden lg:block px-4 py-2 border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:border-red-600 rounded"
            >
              <option value="newest">ថ្មីបំផុត</option>
              <option value="price_low">តម្លៃថោកបំផុត</option>
              <option value="price_high">តម្លៃថ្លៃបំផុត</option>
              <option value="popular">ពេញនិយម</option>
            </select>
          </div>

          {/* Active Filters Tags */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mb-5">
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 border border-red-200 text-xs text-red-600 rounded">
                  {searchTerm}
                  <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-red-800">×</button>
                </span>
              )}
              {selectedCategory && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 border border-red-200 text-xs text-red-600 rounded">
                  {categories.find(c => c.id === selectedCategory)?.name}
                  <button onClick={() => setSelectedCategory('')} className="ml-1 hover:text-red-800">×</button>
                </span>
              )}
              {appliedMinPrice && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 border border-red-200 text-xs text-red-600 rounded">
                  ≥ ${appliedMinPrice}
                  <button onClick={() => {
                    setTempMinPrice('');
                    setAppliedMinPrice('');
                  }} className="ml-1 hover:text-red-800">×</button>
                </span>
              )}
              {appliedMaxPrice && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 border border-red-200 text-xs text-red-600 rounded">
                  ≤ ${appliedMaxPrice}
                  <button onClick={() => {
                    setTempMaxPrice('');
                    setAppliedMaxPrice('');
                  }} className="ml-1 hover:text-red-800">×</button>
                </span>
              )}
              <button 
                onClick={clearFilters}
                className="text-xs text-gray-500 underline hover:text-red-600"
              >
                សម្អាតទាំងអស់
              </button>
            </div>
          )}

          {/* Products Grid */}
          {displayProducts.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 border border-gray-200 rounded">
              <h3 className="text-lg font-medium text-gray-800 mb-2">មិនមានផលិតផលបោះដុំទេ</h3>
              <p className="text-sm text-gray-500 mb-5">
                សូមព្យាយាមស្វែងរកតាមពាក្យផ្សេង ឬជ្រើសរើសប្រភេទផ្សេង
              </p>
              <button 
                onClick={clearFilters}
                className="bg-red-600 text-white px-6 py-2.5 text-sm hover:bg-red-700 rounded"
              >
                សម្អាតតម្រង
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                {displayProducts.map((product, index) => (
                  <div 
                    key={product.id}
                    ref={index === displayProducts.length - 1 ? lastProductRef : null}
                  >
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
              
              {loadingMore && (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              )}
              
              {!hasMore && displayProducts.length > 0 && (
                <div className="text-center py-8 text-gray-500 text-sm border-t border-gray-200 mt-6">
                  អ្នកបានមើលផលិតផលបោះដុំទាំងអស់ {filteredProducts.length} ហើយ
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {showMobileFilters && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 lg:hidden"
          onClick={() => setShowMobileFilters(false)}
        >
          <div 
            className="absolute right-0 top-0 bottom-0 w-4/5 max-w-sm bg-white p-5 overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-200">
              <span className="font-semibold text-gray-800">តម្រង</span>
              <button 
                onClick={() => setShowMobileFilters(false)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Categories - Mobile */}
            <div className="mb-6 pb-5 border-b border-gray-200">
              <h3 className="font-medium text-gray-700 mb-3">ប្រភេទ</h3>
              <div className="space-y-3">
                <div 
                  onClick={() => {
                    setSelectedCategory('');
                    setShowMobileFilters(false);
                  }}
                  className={`flex items-center gap-2 cursor-pointer ${
                    selectedCategory === '' ? 'text-red-600' : 'text-gray-600'
                  }`}
                >
                  <div className={`w-4 h-4 border rounded ${
                    selectedCategory === '' ? 'bg-red-600 border-red-600' : 'border-gray-300'
                  }`}>
                    {selectedCategory === '' && <span className="flex items-center justify-center text-white text-xs">✓</span>}
                  </div>
                  <span className="text-sm">ទាំងអស់</span>
                </div>

                {categories.map((category) => (
                  <div 
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setShowMobileFilters(false);
                    }}
                    className={`flex items-center gap-2 cursor-pointer ${
                      selectedCategory === category.id ? 'text-red-600' : 'text-gray-600'
                    }`}
                  >
                    <div className={`w-4 h-4 border rounded ${
                      selectedCategory === category.id ? 'bg-red-600 border-red-600' : 'border-gray-300'
                    }`}>
                      {selectedCategory === category.id && <span className="flex items-center justify-center text-white text-xs">✓</span>}
                    </div>
                    <span className="text-sm">{category.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Range - Mobile */}
            <div className="mb-6 pb-5 border-b border-gray-200">
              <h3 className="font-medium text-gray-700 mb-3">តម្លៃ</h3>
              <div className="flex gap-2 mb-3">
                <input
                  type="number"
                  placeholder="តិចបំផុត"
                  value={tempMinPrice}
                  onChange={(e) => setTempMinPrice(e.target.value)}
                  className="w-1/2 px-3 py-2 border border-gray-200 text-sm rounded"
                />
                <input
                  type="number"
                  placeholder="ច្រើនបំផុត"
                  value={tempMaxPrice}
                  onChange={(e) => setTempMaxPrice(e.target.value)}
                  className="w-1/2 px-3 py-2 border border-gray-200 text-sm rounded"
                />
              </div>
              <button 
                onClick={() => {
                  applyPriceFilter();
                  setShowMobileFilters(false);
                }}
                className="w-full bg-red-600 text-white py-2.5 text-sm hover:bg-red-700 rounded"
              >
                អនុវត្ត
              </button>
            </div>

            {/* Sort - Mobile */}
            <div className="mb-6 pb-5 border-b border-gray-200">
              <h3 className="font-medium text-gray-700 mb-3">តម្រៀបតាម</h3>
              <div className="space-y-3">
                {[
                  { value: 'newest', label: 'ថ្មីបំផុត' },
                  { value: 'price_low', label: 'តម្លៃថោកបំផុត' },
                  { value: 'price_high', label: 'តម្លៃថ្លៃបំផុត' },
                  { value: 'popular', label: 'ពេញនិយម' }
                ].map((option) => (
                  <div 
                    key={option.value}
                    onClick={() => {
                      setSortBy(option.value);
                      setShowMobileFilters(false);
                    }}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="mobileSort"
                      checked={sortBy === option.value}
                      onChange={() => {}}
                      className="accent-red-600"
                    />
                    <span className="text-sm text-gray-600">{option.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <button
                onClick={() => {
                  clearFilters();
                  setShowMobileFilters(false);
                }}
                className="w-full bg-gray-100 border border-gray-200 py-3 text-sm hover:bg-red-50 hover:text-red-600 hover:border-red-600 rounded"
              >
                សម្អាតតម្រងទាំងអស់
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsTwo;